// src/components/admin/teamTaskbox/TeamTaskbox.js
import React, { useState, useEffect } from 'react';
import {
    Plus, Filter, Search, Grid, List, Calendar, Kanban,
    AlertCircle, CheckCircle, Clock, Play, Pause, XCircle, BarChart3
} from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useEmployees } from '../../../contexts/EmployeeContext';
import teamTasksService from '../../../services/teamTasks.service';
import CreateTaskModal from './CreateTaskModal';
import TaskCard from './TaskCard';
import TaskDetailModal from './TaskDetailModal';
import TaskKanbanBoard from './TaskKanbanBoard';
import TaskCalendarView from './TaskCalendarView';
import TaskAnalyticsWidget from './widgets/TaskAnalyticsWidget';
import TeamWorkloadWidget from './widgets/TeamWorkloadWidget';
import OverdueTasksWidget from './widgets/OverdueTasksWidget';
import MyTasksWidget from './widgets/MyTasksWidget';

const TeamTaskbox = () => {
    const { user } = useAuth();
    const { addNotification } = useNotifications();
    const { employees } = useEmployees();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list', 'kanban', 'calendar', 'dashboard'
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterPriority, setFilterPriority] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    // Fetch tasks
    const fetchTasks = async () => {
        try {
            setLoading(true);
            const filters = {};
            if (filterStatus !== 'all') filters.status = filterStatus;
            if (filterPriority !== 'all') filters.priority = filterPriority;

            const response = await teamTasksService.getTeamTasks(filters);
            if (response.success) {
                setTasks(response.data);
            }
        } catch (error) {
            console.error('Error fetching tasks:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTasks();
    }, [filterStatus, filterPriority]);

    // Filter tasks by search term
    const filteredTasks = tasks.filter(task =>
        task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Group tasks by status for stats
    const taskStats = {
        pending: tasks.filter(t => t.status === 'pending').length,
        in_progress: tasks.filter(t => t.status === 'in_progress').length,
        under_review: tasks.filter(t => t.status === 'under_review').length,
        completed: tasks.filter(t => t.status === 'completed').length,
        on_hold: tasks.filter(t => t.status === 'on_hold').length,
        total: tasks.length,
        overdue: tasks.filter(t => t.overdue_days > 0).length
    };

    const handleCreateTask = async (taskData) => {
        try {
            const response = await teamTasksService.createTeamTask(taskData);
            if (response.success) {
                const createdTask = response.data || response.task;

                // Send notification to each assigned employee
                if (taskData.assigned_employees && Array.isArray(taskData.assigned_employees)) {
                    for (const employeeId of taskData.assigned_employees) {
                        const employee = employees.find(emp => emp.id === employeeId || emp.employee_id === employeeId);

                        await addNotification({
                            userId: employeeId,
                            type: 'task_assigned',
                            title: 'New Task Assigned',
                            message: `${user.name || 'Admin'} assigned you a new task: "${taskData.title}". Due date: ${taskData.due_date ? new Date(taskData.due_date).toLocaleDateString() : 'Not set'}. Priority: ${taskData.priority || 'Medium'}`,
                            icon: 'ClipboardCheck',
                            color: 'blue',
                            data: {
                                task_id: createdTask?.id || createdTask?.task_id,
                                task_title: taskData.title,
                                due_date: taskData.due_date,
                                priority: taskData.priority,
                                assigned_by: user.name
                            }
                        });
                    }
                }

                setShowCreateModal(false);
                fetchTasks();
                alert('Team task created successfully!');
            }
        } catch (error) {
            console.error('Error creating task:', error);
            alert('Failed to create task');
        }
    };

    const handleTaskClick = (task) => {
        setSelectedTask(task);
        setShowDetailModal(true);
    };

    const handleTaskUpdate = () => {
        fetchTasks();
        setShowDetailModal(false);
    };

    const handleCloneTask = async (taskId) => {
        try {
            const response = await teamTasksService.cloneTeamTask(taskId);
            if (response.success) {
                fetchTasks();
                alert('Task cloned successfully!');
            }
        } catch (error) {
            console.error('Error cloning task:', error);
            alert('Failed to clone task');
        }
    };

    const handleDeleteTask = async (taskId) => {
        if (!window.confirm('Are you sure you want to delete this task?')) return;

        try {
            const response = await teamTasksService.deleteTeamTask(taskId);
            if (response.success) {
                fetchTasks();
                setShowDetailModal(false);
                alert('Task deleted successfully!');
            }
        } catch (error) {
            console.error('Error deleting task:', error);
            alert('Failed to delete task');
        }
    };

    const getStatusIcon = (status) => {
        const icons = {
            pending: <Clock className="w-4 h-4" />,
            in_progress: <Play className="w-4 h-4" />,
            under_review: <AlertCircle className="w-4 h-4" />,
            completed: <CheckCircle className="w-4 h-4" />,
            on_hold: <Pause className="w-4 h-4" />
        };
        return icons[status] || <Clock className="w-4 h-4" />;
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Team Taskbox
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Create and manage team tasks
                    </p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="tour-taskbox-create flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                    <Plus className="w-5 h-5" />
                    Add New Team Task
                </button>
            </div>

            {/* Stats Cards */}
            <div className="tour-taskbox-stats grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4 text-gray-600" />
                        <span className="text-xs text-gray-600 dark:text-gray-400">Pending</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{taskStats.pending}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Play className="w-4 h-4 text-blue-600" />
                        <span className="text-xs text-gray-600 dark:text-gray-400">In Progress</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">{taskStats.in_progress}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-yellow-600" />
                        <span className="text-xs text-gray-600 dark:text-gray-400">Under Review</span>
                    </div>
                    <p className="text-2xl font-bold text-yellow-600">{taskStats.under_review}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-xs text-gray-600 dark:text-gray-400">Completed</span>
                    </div>
                    <p className="text-2xl font-bold text-green-600">{taskStats.completed}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Pause className="w-4 h-4 text-orange-600" />
                        <span className="text-xs text-gray-600 dark:text-gray-400">On Hold</span>
                    </div>
                    <p className="text-2xl font-bold text-orange-600">{taskStats.on_hold}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <XCircle className="w-4 h-4 text-red-600" />
                        <span className="text-xs text-gray-600 dark:text-gray-400">Overdue</span>
                    </div>
                    <p className="text-2xl font-bold text-red-600">{taskStats.overdue}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-4 h-4 text-purple-600" />
                        <span className="text-xs text-gray-600 dark:text-gray-400">Total Tasks</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-600">{taskStats.total}</p>
                </div>
            </div>

            {/* Filters & Search */}
            <div className="tour-taskbox-filters bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="flex flex-wrap gap-4 items-center justify-between">
                    <div className="flex gap-4 items-center flex-1">
                        {/* Search */}
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search tasks..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        {/* Status Filter */}
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="all">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="in_progress">In Progress</option>
                            <option value="under_review">Under Review</option>
                            <option value="completed">Completed</option>
                            <option value="on_hold">On Hold</option>
                        </select>

                        {/* Priority Filter */}
                        <select
                            value={filterPriority}
                            onChange={(e) => setFilterPriority(e.target.value)}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="all">All Priority</option>
                            <option value="immediate">Immediate</option>
                            <option value="urgent">Urgent</option>
                            <option value="next_in_line">Next in Line</option>
                            <option value="scheduled">Scheduled</option>
                            <option value="deferred">Deferred</option>
                        </select>
                    </div>

                    {/* View Mode Toggle */}
                    <div className="tour-taskbox-views flex gap-2">
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`p-2 rounded-lg transition-colors ${
                                viewMode === 'grid'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                            }`}
                            title="Grid View"
                        >
                            <Grid className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`p-2 rounded-lg transition-colors ${
                                viewMode === 'list'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                            }`}
                            title="List View"
                        >
                            <List className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setViewMode('kanban')}
                            className={`p-2 rounded-lg transition-colors ${
                                viewMode === 'kanban'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                            }`}
                            title="Kanban Board"
                        >
                            <Kanban className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setViewMode('calendar')}
                            className={`p-2 rounded-lg transition-colors ${
                                viewMode === 'calendar'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                            }`}
                            title="Calendar View"
                        >
                            <Calendar className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setViewMode('dashboard')}
                            className={`p-2 rounded-lg transition-colors ${
                                viewMode === 'dashboard'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                            }`}
                            title="Dashboard & Analytics"
                        >
                            <BarChart3 className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Tasks Display */}
            {loading ? (
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            ) : viewMode === 'dashboard' ? (
                /* Dashboard View with Widgets */
                <div className="space-y-6">
                    {/* Widgets Row 1 */}
                    {(user.role === 'admin' || user.role === 'manager' || user.role === 'Admin' || user.role === 'Manager') ? (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <TaskAnalyticsWidget tasks={tasks} />
                            <TeamWorkloadWidget tasks={tasks} />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
                            <TaskAnalyticsWidget tasks={tasks} />
                        </div>
                    )}

                    {/* Widgets Row 2 */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <OverdueTasksWidget tasks={tasks} onTaskClick={handleTaskClick} />
                        <MyTasksWidget tasks={tasks} onTaskClick={handleTaskClick} />
                    </div>
                </div>
            ) : viewMode === 'kanban' ? (
                /* Kanban Board View */
                <TaskKanbanBoard
                    tasks={filteredTasks}
                    onTaskUpdate={fetchTasks}
                    onTaskClick={handleTaskClick}
                />
            ) : viewMode === 'calendar' ? (
                /* Calendar View */
                <TaskCalendarView
                    tasks={filteredTasks}
                    onTaskClick={handleTaskClick}
                />
            ) : filteredTasks.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
                    <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        No tasks found
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        {searchTerm ? 'Try adjusting your search or filters' : 'Create your first team task to get started'}
                    </p>
                    {!searchTerm && (
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                        >
                            Create Team Task
                        </button>
                    )}
                </div>
            ) : (
                /* Grid/List View */
                <div className={
                    viewMode === 'grid'
                        ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
                        : 'space-y-4'
                }>
                    {filteredTasks.map(task => (
                        <TaskCard
                            key={task.id}
                            task={task}
                            viewMode={viewMode}
                            onClick={() => handleTaskClick(task)}
                            onClone={() => handleCloneTask(task.id)}
                            onDelete={() => handleDeleteTask(task.id)}
                        />
                    ))}
                </div>
            )}

            {/* Create Task Modal */}
            {showCreateModal && (
                <CreateTaskModal
                    onClose={() => setShowCreateModal(false)}
                    onSubmit={handleCreateTask}
                />
            )}

            {/* Task Detail Modal */}
            {showDetailModal && selectedTask && (
                <TaskDetailModal
                    taskId={selectedTask.id}
                    onClose={() => setShowDetailModal(false)}
                    onUpdate={handleTaskUpdate}
                    onDelete={() => handleDeleteTask(selectedTask.id)}
                    onClone={() => handleCloneTask(selectedTask.id)}
                />
            )}
        </div>
    );
};

export default TeamTaskbox;
